import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {Hub} from 'aws-amplify/utils'

interface ProgressiveAuthContextValue {
    availableChallenges: string[]
    isChallengeReady: boolean
    nextStep: string | null
    setChallenges: (challenges: string[], nextStep: string | null) => void
    resetChallenges: () => void
}

const ProgressiveAuthContext = createContext<ProgressiveAuthContextValue | null>(null)

export function ProgressiveAuthProvider({children}: { children: ReactNode }) {
    const [availableChallenges, setAvailableChallenges] = useState<string[]>([])
    const [nextStep, setNextStep] = useState<string | null>(null)
    const [isChallengeReady, setIsChallengeReady] = useState(false)

    const setProgressiveChallenges = useCallback((challenges: string[], step: string | null) => {
        setAvailableChallenges(challenges)
        setNextStep(step)
        setIsChallengeReady(true)
    }, [])

    const resetProgressiveChallenges = useCallback(() => {
        setAvailableChallenges([])
        setNextStep(null)
        setIsChallengeReady(false)
    }, [])

    useEffect(() => {
        const unsubscribe = Hub.listen('auth', ({payload}) => {
            if (payload.event === 'signedOut') {
                resetProgressiveChallenges()
            }
        })

        return unsubscribe
    }, [resetProgressiveChallenges])

    const value = useMemo<ProgressiveAuthContextValue>(() => ({
        availableChallenges,
        isChallengeReady,
        nextStep,
        setChallenges: setProgressiveChallenges,
        resetChallenges: resetProgressiveChallenges
    }), [
        availableChallenges,
        isChallengeReady,
        nextStep,
        resetProgressiveChallenges,
        setProgressiveChallenges
    ])

    return (
        <ProgressiveAuthContext.Provider value={value}>
            {children}
        </ProgressiveAuthContext.Provider>
    )
}

export function useProgressiveAuth(): ProgressiveAuthContextValue {
    const ctx = useContext(ProgressiveAuthContext)

    if (!ctx) {
        throw new Error('useProgressiveAuth must be used within ProgressiveAuthProvider')
    }

    return ctx
}
